import styled, { css } from "styled-components";

import { color } from "../../theme/color";

/** How a button looks: the main action, a normal one, or a quiet one without a border. */
export type ButtonKind = "primary" | "secondary" | "quiet" | "danger";

const BUTTON_KINDS: Record<ButtonKind, ReturnType<typeof css>> = {
    primary: css`
        background: ${color.accent};
        color: ${color.onAccent};
        border: 1px solid ${color.accent};

        &:hover:not(:disabled) {
            background: ${color.accentHover};
        }
    `,
    secondary: css`
        background: ${color.background};
        color: ${color.text};
        border: 1px solid ${color.borderStrong};
    `,
    quiet: css`
        background: transparent;
        color: ${color.accent};
        border: 1px solid transparent;
    `,
    danger: css`
        background: ${color.background};
        color: ${color.danger};
        border: 1px solid ${color.borderStrong};
    `,
};

/** A button with text, at least 44px high so it's easy to tap. */
export const Button = styled.button<{ $kind?: ButtonKind }>`
    min-height: 44px;
    padding: 0 16px;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    cursor: pointer;
    white-space: nowrap;

    ${({ $kind = "secondary" }) => BUTTON_KINDS[$kind]}

    &:disabled {
        color: ${color.textFaint};
        cursor: default;
    }
`;

/** A square button with only an icon. Give it an `aria-label`. It's highlighted while `$active`, or while what it opens is open. */
export const IconButton = styled.button<{ $active?: boolean; $bordered?: boolean }>`
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    border-radius: 10px;
    border: 1px solid ${({ $bordered }) => ($bordered ? color.borderStrong : "transparent")};
    background: ${({ $active }) => ($active ? color.accentSoft : "transparent")};
    color: ${({ $active }) => ($active ? color.accent : color.text)};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &[aria-expanded="true"] {
        background: ${color.accentSoft};
        color: ${color.accent};
    }

    &:disabled {
        color: ${color.textFaint};
        cursor: default;
    }
`;

export const FieldLabel = styled.label`
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: ${color.textSoft};
`;

/** A small heading in capitals, above a list or a group of settings. */
export const SmallHeading = styled.h2`
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${color.textMuted};
`;

/** The key of a song, always visible on a chord sheet. */
export const KeyBadge = styled.span`
    min-width: 34px;
    height: 30px;
    padding: 0 9px;
    border-radius: 8px;
    background: ${color.accentSoft};
    color: ${color.accent};
    font-size: 15px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

/** A small rounded label, like a version count or "this setlist only". */
export const Pill = styled.span<{ $tone?: "accent" | "setlistOnly" | "success" }>`
    font-size: 12px;
    font-weight: 600;
    border-radius: 999px;
    padding: 2px 9px;
    white-space: nowrap;
    color: ${({ $tone = "accent" }) => color[$tone]};
    background: ${({ $tone = "accent" }) => color[`${$tone}Soft`]};
`;

/** Text that only screen readers read. */
export const Hidden = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
`;

/** One option in a list of choices, with a radio button. The chosen one is marked. */
export const Choice = styled.label`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-height: 48px;
    padding: 13px 12px;
    border: 1px solid ${color.border};
    border-radius: 12px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;

    &:has(input:checked) {
        border-color: ${color.accent};
        background: ${color.accentSofter};
    }

    input {
        width: 20px;
        height: 20px;
        margin: 0;
        flex-shrink: 0;
        accent-color: ${color.accent};
    }
`;

/** Text under a choice that explains it. */
export const ChoiceDetail = styled.span`
    display: block;
    margin-top: 3px;
    font-size: 14px;
    font-weight: 400;
    color: ${color.textMuted};
`;
